import React, { useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import {
  Badge,
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  IconButton,
  ListItemButton,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { HeaderIconButtonStyled } from "@components/MainNavigation/Header/Header.styles.tsx";
import { queryClient } from "@config/queryClient";
import { markNotificationsSeen } from "@services/notificationsApi";
import { runTransaction } from "@telemetry/runTransaction";
import { normalizeRole } from "@utils/roles";
import { useTranslation } from "react-i18next";
import {
  AnchorStyled,
  formatDate,
  getNotificationItemTitleStyle,
  getNotificationsHeaderStyle,
  getStatusChipSx,
} from "./NotificationsDropdown.styles";
import type { NotificationItem, NotificationsDropdownProps } from "./NotificationsDropdown.types";

const isVesselCallClosedNotification = (type?: string) => type === "VESSEL_CALL_CLOSED";

const MARK_NOTIFICATIONS_SEEN_TRANSACTION_CONTEXT = {
  module: "notifications",
  screen: "Notifications Dropdown",
  action: "mark_notifications_seen",
  entityType: "notification",
};

const NotificationsDropdown = ({
  notifications,
  onViewAll,
  onItemClick,
  hasNew = false,
  onOpen,
}: NotificationsDropdownProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isCustomsNotification = (type?: string) => type?.toUpperCase().includes("CUSTOMS");

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // Local copy for optimistic updates
  const [localNotifications, setLocalNotifications] = useState<NotificationItem[]>(
    notifications ?? [],
  );

  const revertSnapshotRef = useRef<NotificationItem[] | null>(null);

  // Sync when prop changes (unless in optimistic update)
  useEffect(() => {
    if (!revertSnapshotRef.current && notifications) {
      setLocalNotifications(notifications);
    }
  }, [notifications]);

  // Optimistically mark one as seen
  const optimisticallyMarkSeen = async (id: string) => {
    const alreadySeen = localNotifications.find((n) => n.id === id && n.seen);
    if (alreadySeen) return;

    revertSnapshotRef.current = localNotifications;
    setLocalNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, seen: true } : n)));

    try {
      await runTransaction(
        {
          ...MARK_NOTIFICATIONS_SEEN_TRANSACTION_CONTEXT,
          entityId: id,
        },
        (transactionId) =>
          markNotificationsSeen({
            service: "bff-notification",
            ids: [id],
            ...(transactionId ? { transactionId } : {}),
          }),
      );
      // Invalidate queries so external lists refresh
      queryClient.invalidateQueries({ queryKey: ["notifications.list"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["notifications.dropdown"], exact: false });
      revertSnapshotRef.current = null;
    } catch {
      // Revert on failure
      if (revertSnapshotRef.current) {
        setLocalNotifications(revertSnapshotRef.current);
        revertSnapshotRef.current = null;
      }
    }
  };

  const items = useMemo(() => {
    if (!localNotifications?.length) return [];
    const toTime = (n: NotificationItem) => {
      const s = n.submittedAt ?? n.date;
      return s ? new Date(s).getTime() : 0;
    };
    return [...localNotifications].sort((a, b) => toTime(b) - toTime(a)).slice(0, 3);
  }, [localNotifications]);

  return (
    <AnchorStyled>
      <HeaderIconButtonStyled
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          const nextOpen = !open;
          setAnchorEl(nextOpen ? e.currentTarget : null);
          if (nextOpen) onOpen?.();
        }}
        color="inherit"
        aria-label="Notifications"
      >
        <Badge
          variant="dot"
          color={hasNew ? "error" : "default"}
          overlap="circular"
          data-testid="notifications-badge"
        >
          <NotificationsOutlinedIcon fontSize="medium" />
        </Badge>
      </HeaderIconButtonStyled>

      {/* Dropdown */}
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        disablePortal
        className="z-50"
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        data-testid="notifications-dropdown"
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper elevation={4} className="w-[546px] max-w-[90vw] !rounded-[12px] overflow-hidden">
            {/* Header */}
            <Box className="flex items-center justify-between px-4 pt-3">
              <Typography variant="inherit" style={getNotificationsHeaderStyle(theme)}>
                {t("common.notifications")}
              </Typography>
              <IconButton
                size="small"
                aria-label="Close notifications"
                onClick={() => setAnchorEl(null)}
                color="inherit"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Notifications */}
            <Box className="max-h-[60vh] overflow-y-auto">
              {items.length === 0 ? (
                <Box className="px-4 py-6 text-sm text-gray-500">You’re all caught up.</Box>
              ) : (
                items.map((n: NotificationItem, idx) => (
                  <Box key={n.id}>
                    <ListItemButton
                      className="px-4 py-1"
                      onClick={async () => {
                        setAnchorEl(null);
                        onItemClick?.(n);
                        await optimisticallyMarkSeen(n.id);
                      }}
                      data-testid={`notification-${idx}`}
                    >
                      <Box className="w-full">
                        <Chip
                          size="small"
                          label={n.status}
                          className="mb-2 rounded-full"
                          sx={getStatusChipSx(theme, n.status)}
                          data-testid={`notification-${idx}-status`}
                        />
                        <Typography
                          variant="inherit"
                          style={{
                            ...getNotificationItemTitleStyle(theme),
                            fontWeight: n.seen === false ? 700 : 400,
                            whiteSpace: "pre-line",
                          }}
                          className="text-sm leading-5"
                          data-testid={`notification-${idx}-title`}
                        >
                          {n.title}
                        </Typography>
                        <Box
                          className="mt-1 flex items-center justify-between"
                          data-testid={`notification-${idx}-subtitle`}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {n.status === "Cancelled"
                              ? t("common.cancelledBy")
                              : t("common.submittedBy")}{" "}
                            <>
                              <Box
                                component="span"
                                sx={{
                                  color: (theme) => theme.palette.text.primary,
                                  fontWeight: 500,
                                }}
                              >
                                {n.submittedByRole
                                  ? t(`roles.${normalizeRole(n.submittedByRole)}`, {
                                      defaultValue: n.submittedByRole,
                                    })
                                  : null}
                              </Box>
                              {n.submittedBy === "SYSTEM" || isCustomsNotification(n.type)
                                ? ""
                                : isVesselCallClosedNotification(n.type)
                                  ? ", "
                                  : " - "}
                              <span className="font-medium">{n.submittedBy}</span>
                            </>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(n.date)}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItemButton>

                    {idx < items.length - 1 && (
                      <Box className="px-4">
                        <Divider />
                      </Box>
                    )}
                  </Box>
                ))
              )}
            </Box>

            {/* Footer */}
            <Box className="px-4">
              <Divider />
            </Box>
            <Box className="flex items-center justify-center px-4 py-3">
              <button
                type="button"
                onClick={() => {
                  setAnchorEl(null);
                  onViewAll?.();
                }}
                className="text-sm font-medium hover:underline cursor-pointer"
                style={{ color: theme.palette.text.brand }}
              >
                {t("common.view-all")}
              </button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </AnchorStyled>
  );
};

export default NotificationsDropdown;
